import React, { useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { adminApi, setAdminToken, getAdminCsrf } from "../apiAdmin";
import { AdminAuthContext, AdminUser } from "../context/AdminAuthContext";
import {
  canAddFaceIdHere,
  canUseFaceId,
  deviceLabel,
  dismissFaceIdOffer,
  faceIdAlreadyHere,
  faceIdCancelled,
  faceIdName,
  faceIdOfferDismissed,
  faceIdWorksHere,
  rememberFaceIdHere,
  startAuthentication,
  startRegistration,
} from "../lib/adminFaceId";
import "./AdminLoginStyles.css";

// Admin sign-in: Face ID / Touch ID (a passkey) on a device that has it, or
// password, then a six-digit code from an authenticator app — the backup door,
// and the only way to turn Face ID on. The server decides which screen comes
// next (lib/admin/signin.js, lib/admin/passkeys.js):
//   { accessToken, user }                      → signed in
//   … + passkeySetupToken                      → password + code done: offer Face ID here
//   { step: "totp", challengeToken }           → ask for the code
//   { step: "enroll", enrollToken, secret, … } → first time: link the app
//   429 { locked: true }                       → locked; password + code together (or Face ID) still works
// A server from before Face ID never sends passkeySetupToken, so this page
// works against both.

type Session = { accessToken: string; user: AdminUser };
type Stage =
  | { name: "password" }
  | { name: "totp"; challengeToken: string }
  | { name: "enroll"; enrollToken: string; secret: string; otpauthUrl: string }
  | { name: "recovery"; codes: string[]; session: Session; setupToken?: string }
  | { name: "faceIdOffer"; setupToken: string; session: Session };

// Face-scan outline, brand green — no emoji.
const FACE_GLYPH = (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#24C961" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2M9 9v1M15 9v1M12 9v4h-1M9 15.5c1.7 1.3 4.3 1.3 6 0" />
  </svg>
);

const grouped = (secret: string) => secret.replace(/(.{4})/g, "$1 ").trim();

export default function AdminLogin() {
  const { login } = useContext(AdminAuthContext);
  const navigate = useNavigate();
  // ?next= is set by RequireAdminAuth and by an expired session
  // (apiAdmin endAdminSessionLocally). Only an in-app /admin/ page is followed.
  const next = new URLSearchParams(useLocation().search).get("next") || "";
  const returnTo = next.startsWith("/admin/") && !next.startsWith("/admin/login") ? next : "/admin/dashboard";
  const [stage, setStage] = useState<Stage>({ name: "password" });
  const [form, setForm] = useState({ email: "", password: "" });
  const [code, setCode] = useState("");
  const [locked, setLocked] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Face ID leads only where it already worked; elsewhere it is a second
  // button under the password form (a synced passkey can still be on here).
  const [faceIdOk] = useState(canUseFaceId);
  const [faceIdFirst, setFaceIdFirst] = useState(() => canUseFaceId() && faceIdWorksHere());
  const faceId = faceIdName();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const enter = (session: Session) => {
    setAdminToken(session.accessToken);
    login(session.user, session.accessToken);
    navigate(returnTo, { replace: true });
  };

  const backToPassword = (message: string, isLocked = false) => {
    setStage({ name: "password" });
    setCode("");
    setUseRecovery(false);
    setLocked(isLocked);
    setError(message);
  };

  // After a password + code sign-in: offer Face ID once on a device that has
  // it, unless it already works here or "Not now" was tapped here.
  const offerFaceIdOrEnter = async (session: Session, setupToken?: string) => {
    if (setupToken && !faceIdWorksHere() && !faceIdOfferDismissed() && (await canAddFaceIdHere())) {
      setStage({ name: "faceIdOffer", setupToken, session });
      return;
    }
    enter(session);
  };

  // One place reads every answer the sign-in calls can give.
  const handle = (data: any) => {
    setError("");
    setCode("");
    if (data?.accessToken && data?.user) {
      const session = { accessToken: data.accessToken, user: data.user };
      const setupToken = typeof data.passkeySetupToken === "string" ? data.passkeySetupToken : undefined;
      if (Array.isArray(data.recoveryCodes) && data.recoveryCodes.length) {
        setStage({ name: "recovery", codes: data.recoveryCodes, session, setupToken });
      } else {
        void offerFaceIdOrEnter(session, setupToken);
      }
    } else if (data?.step === "totp") {
      setStage({ name: "totp", challengeToken: data.challengeToken });
    } else if (data?.step === "enroll") {
      setStage({ name: "enroll", enrollToken: data.enrollToken, secret: data.secret, otpauthUrl: data.otpauthUrl });
    } else {
      setError("Unexpected answer from the server.");
    }
  };

  const fail = (err: any) => {
    const status = err?.response?.status;
    const body = err?.response?.data;
    const msg = body?.msg || "Could not sign in. Try again.";
    if (status === 429 && body?.locked) return backToPassword(msg, true);
    if (status === 401 && body?.code === "step_expired") return backToPassword(msg);
    if (status === 409 && body?.code === "already_enrolled") return backToPassword(msg);
    setError(msg);
  };

  const post = async (path: string, payload: Record<string, string>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await getAdminCsrf();
      const { data } = await adminApi.post(path, payload);
      handle(data);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    post("/login", locked && code ? { ...form, code } : form);
  };
  const submitCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.name === "totp") post("/login/totp", { challengeToken: stage.challengeToken, code });
    if (stage.name === "enroll") post("/login/enroll", { enrollToken: stage.enrollToken, code });
  };

  const faceIdFailed = (err: any) => {
    const body = err?.response?.data;
    if (body?.code === "unknown_passkey") {
      rememberFaceIdHere(false);
      setFaceIdFirst(false);
    }
    if (err?.response?.status === 429) return fail(err);
    if (body?.code === "step_expired") return setError(`That took too long. Try ${faceId} again.`);
    setError(body?.msg || `${faceId} didn't go through. Try again, or use your password.`);
  };

  const signInWithFaceId = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await getAdminCsrf();
      const { data: start } = await adminApi.post("/login/passkey/options", {});
      const response = await startAuthentication({ optionsJSON: start.options });
      const { data } = await adminApi.post("/login/passkey", { challengeToken: start.challengeToken, response });
      rememberFaceIdHere();
      handle(data);
    } catch (err) {
      if (!faceIdCancelled(err)) faceIdFailed(err);
    } finally {
      setBusy(false);
    }
  };

  const turnOnFaceId = async () => {
    if (busy || stage.name !== "faceIdOffer") return;
    const { setupToken, session } = stage;
    setBusy(true);
    setError("");
    try {
      await getAdminCsrf();
      const { data: start } = await adminApi.post("/login/passkey/register/options", { setupToken });
      const response = await startRegistration({ optionsJSON: start.options });
      await adminApi.post("/login/passkey/register", { setupToken, challengeToken: start.challengeToken, response, label: deviceLabel() });
      rememberFaceIdHere();
      enter(session);
    } catch (err: any) {
      // Already on this device, or the offer ran out: they are signed in either way.
      if (faceIdAlreadyHere(err)) {
        rememberFaceIdHere();
        enter(session);
      } else if (err?.response?.data?.code === "step_expired") {
        enter(session);
      } else if (!faceIdCancelled(err)) {
        setError(`${faceId} couldn't be turned on. You can try again next time you sign in.`);
      }
    } finally {
      setBusy(false);
    }
  };

  const notNowFaceId = () => {
    if (stage.name !== "faceIdOffer") return;
    dismissFaceIdOffer();
    enter(stage.session);
  };

  const copyCodes = async (codes: string[]) => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const codeInput = (placeholder: string, recovery = false) => (
    <input
      id="adm-login-code"
      name="code"
      type="text"
      inputMode={recovery ? "text" : "numeric"}
      autoComplete="one-time-code"
      autoCapitalize="characters"
      autoCorrect="off"
      spellCheck={false}
      placeholder={placeholder}
      value={code}
      onChange={(e) => setCode(e.target.value)}
      maxLength={recovery ? 12 : 7}
      autoFocus
      required
    />
  );

  return (
    <div id="adm-login-root">
      {stage.name === "password" && faceIdFirst && (
        <div id="adm-login-box">
          <div className="adm-faceid-glyph">{FACE_GLYPH}</div>
          <h2 id="adm-login-title">Admin Login</h2>
          <button id="adm-login-btn" type="button" onClick={signInWithFaceId} disabled={busy}>
            {busy ? "Checking…" : `Sign in with ${faceId}`}
          </button>
          <button type="button" className="adm-login-link" onClick={() => { setFaceIdFirst(false); setError(""); }}>
            Use password instead
          </button>
          {error && <p id="adm-login-error" role="alert">{error}</p>}
        </div>
      )}

      {stage.name === "password" && !faceIdFirst && (
        <form id="adm-login-box" onSubmit={submitPassword}>
          <h2 id="adm-login-title">Admin Login</h2>
          <input id="adm-login-email" type="email" name="email" placeholder="Email" autoComplete="username" value={form.email} onChange={handleChange} required />
          <input id="adm-login-password" type="password" name="password" placeholder="Password" autoComplete="current-password" value={form.password} onChange={handleChange} required />
          {locked && (
            <input
              id="adm-login-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Authenticator code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={12}
            />
          )}
          <button id="adm-login-btn" type="submit" disabled={busy}>{busy ? "Checking…" : "Login"}</button>
          {faceIdOk && (
            <button type="button" className="adm-login-secondary adm-login-faceid-alt" onClick={signInWithFaceId} disabled={busy}>
              Sign in with {faceId}
            </button>
          )}
          {error && <p id="adm-login-error" role="alert">{error}</p>}
        </form>
      )}

      {stage.name === "totp" && (
        <form id="adm-login-box" onSubmit={submitCode}>
          <h2 id="adm-login-title">Enter your code</h2>
          <p className="adm-login-hint">
            {useRecovery ? "One of the recovery codes you saved. Each works once." : "The 6-digit code for Request App Admin in your authenticator app."}
          </p>
          {useRecovery ? codeInput("XXXXX-XXXXX", true) : codeInput("123 456")}
          <button id="adm-login-btn" type="submit" disabled={busy}>{busy ? "Checking…" : "Sign in"}</button>
          <button type="button" className="adm-login-link" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}>
            {useRecovery ? "Use the app code instead" : "Lost your phone? Use a recovery code"}
          </button>
          <button type="button" className="adm-login-link" onClick={() => backToPassword("")}>Back</button>
          {error && <p id="adm-login-error" role="alert">{error}</p>}
        </form>
      )}

      {stage.name === "enroll" && (
        <form id="adm-login-box" className="adm-login-wide" onSubmit={submitCode}>
          <h2 id="adm-login-title">Set up two-step sign-in</h2>
          <p className="adm-login-hint">
            Scan this with your authenticator app (iPhone Passwords, 1Password, Google Authenticator), then type the 6-digit code it shows.
          </p>
          <div id="adm-login-qr">
            <QRCode value={stage.otpauthUrl} size={176} />
          </div>
          <p className="adm-login-hint">Can't scan? Enter this key by hand:</p>
          <code id="adm-login-secret">{grouped(stage.secret)}</code>
          {codeInput("123 456")}
          <button id="adm-login-btn" type="submit" disabled={busy}>{busy ? "Checking…" : "Turn on and sign in"}</button>
          <button type="button" className="adm-login-link" onClick={() => backToPassword("")}>Back</button>
          {error && <p id="adm-login-error" role="alert">{error}</p>}
        </form>
      )}

      {stage.name === "recovery" && (
        <div id="adm-login-box" className="adm-login-wide">
          <h2 id="adm-login-title">Save your recovery codes</h2>
          <p className="adm-login-hint">
            If you lose your phone, one of these signs you in. Each works once. They are shown only now.
          </p>
          <ol id="adm-login-recovery">
            {stage.codes.map((c) => (
              <li key={c}><code>{c}</code></li>
            ))}
          </ol>
          <button type="button" className="adm-login-secondary" onClick={() => copyCodes(stage.codes)}>
            {copied ? "Copied" : "Copy all"}
          </button>
          <label id="adm-login-saved">
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            I saved them somewhere safe
          </label>
          <button id="adm-login-btn" type="button" disabled={!saved} onClick={() => void offerFaceIdOrEnter(stage.session, stage.setupToken)}>
            Continue to the dashboard
          </button>
        </div>
      )}

      {stage.name === "faceIdOffer" && (
        <div id="adm-login-box">
          <div className="adm-faceid-glyph">{FACE_GLYPH}</div>
          <h2 id="adm-login-title">Turn on {faceId}</h2>
          <p className="adm-login-hint">Next time, sign in with one tap.</p>
          <button id="adm-login-btn" type="button" onClick={turnOnFaceId} disabled={busy}>
            {busy ? "Turning on…" : `Turn on ${faceId}`}
          </button>
          <button type="button" className="adm-login-link" onClick={notNowFaceId} disabled={busy}>
            Not now
          </button>
          {error && <p id="adm-login-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
